(() => {
  const productSelector = '.thb-product-detail';
  const clean = (value) => (value || '').replace(/\s+/g, ' ').trim();

  const escapeExpression = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const conciseOption = (value) => {
    const full = clean(value);
    const title = clean(document.querySelector('.product-title')?.textContent);
    if (!title) return full;
    return clean(full.replace(new RegExp(`^${escapeExpression(title)}\\s*[-–—:]\\s*`, 'i'), '')) || full;
  };

  const getMainVariantPicker = (product) => product.querySelector('variant-selects:not([data-sticky])');

  const getVariantData = (picker) => {
    try {
      return JSON.parse(picker?.querySelector('script[type="application/json"]')?.textContent || '[]');
    } catch (_error) {
      return [];
    }
  };

  const centerSelectedGeometry = () => {
    document.querySelectorAll('[data-section-key="geometry"] .frengee-geometry-table-container').forEach((container) => {
      const selected = container.querySelector('thead .sierra-geometry-selected');
      if (!selected || container.scrollWidth <= container.clientWidth) return;
      const left = selected.offsetLeft - ((container.clientWidth - selected.offsetWidth) / 2);
      container.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    });
  };

  const choiceImage = (variants, optionIndex, value) => {
    const variant = variants.find((item) => item.options?.[optionIndex] === value && (item.featured_image || item.featured_media));
    return variant?.featured_image?.src || variant?.featured_media?.preview_image?.src || '';
  };

  const updateChoiceGroup = (fieldset) => {
    const select = fieldset.querySelector('select');
    const choices = fieldset.querySelector('.sierra-option-choices');
    if (!select || !choices) return;

    [...choices.querySelectorAll('button[data-value]')].forEach((button) => {
      const option = [...select.options].find((item) => item.value === button.dataset.value);
      const selected = select.value === button.dataset.value;
      const disabled = Boolean(option?.disabled);
      if (button.disabled !== disabled) button.disabled = disabled;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });

    const count = fieldset.querySelector('.sierra-option-count');
    if (count) {
      const available = [...select.options].filter((option) => !option.disabled).length;
      count.textContent = `${available} of ${select.options.length} available`;
    }
  };

  const enhanceOption = (picker, fieldset) => {
    const select = fieldset.querySelector('select');
    if (!select || fieldset.dataset.sierraChoices === 'true') return;

    const optionIndex = Math.max(0, Number((fieldset.dataset.index || 'option1').replace('option', '')) - 1);
    const handle = (fieldset.dataset.handle || '').toLowerCase();
    const isSize = /size|frame/.test(handle);
    const isColor = /colou?r|finish/.test(handle);
    const optionValues = [...select.options].map((option) => clean(option.textContent));
    const isLarge = !isSize && !isColor && (optionValues.length > 8 || optionValues.some((value) => value.length > 48));
    const variants = getVariantData(picker);
    const label = fieldset.querySelector('.form__label');
    const choices = document.createElement('div');
    choices.className = `sierra-option-choices ${isColor ? 'sierra-option-choices--color' : (isLarge ? 'sierra-option-choices--large' : 'sierra-option-choices--size')}`;
    choices.setAttribute('role', 'group');
    choices.setAttribute('aria-label', clean(label?.textContent) || select.name);

    [...select.options].forEach((option) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sierra-option-choice';
      button.dataset.value = option.value;
      button.disabled = option.disabled;

      if (isColor) {
        const preview = document.createElement('span');
        preview.className = 'sierra-option-swatch';
        const image = choiceImage(variants, optionIndex, option.value);
        if (image) preview.style.backgroundImage = `url("${image.replace(/"/g, '%22')}")`;
        preview.setAttribute('aria-hidden', 'true');
        button.appendChild(preview);
      }

      const text = document.createElement('span');
      text.className = 'sierra-option-choice-label';
      if (isLarge) {
        const compact = conciseOption(option.textContent);
        const parts = compact.split(',').map(clean).filter(Boolean);
        const primary = document.createElement('strong');
        primary.className = 'sierra-option-choice-primary';
        primary.textContent = parts.shift() || compact;
        const meta = document.createElement('small');
        meta.className = 'sierra-option-choice-meta';
        meta.textContent = parts.join(' · ');
        text.append(primary, meta);
        button.setAttribute('aria-label', clean(option.textContent));
        button.title = clean(option.textContent);
      } else {
        text.textContent = option.textContent;
      }
      button.appendChild(text);
      button.addEventListener('click', () => {
        if (button.disabled || select.value === button.dataset.value) return;
        const fieldsets = [...picker.querySelectorAll('fieldset')];
        const values = fieldsets.map((item, index) => {
          if (index === optionIndex) return button.dataset.value;
          return item.querySelector('select')?.value || item.querySelector('input:checked')?.value;
        });
        const exactVariant = variants.find((variant) => variant.options?.every((value, index) => value === values[index]));
        const fallbackVariant = exactVariant?.available
          ? exactVariant
          : variants.find((variant) => variant.available && variant.options?.[optionIndex] === button.dataset.value);

        if (fallbackVariant) {
          fieldsets.forEach((item, index) => {
            const fieldSelect = item.querySelector('select');
            const fieldInput = item.querySelector(`input[value="${CSS.escape(fallbackVariant.options[index])}"]`);
            if (fieldSelect) fieldSelect.value = fallbackVariant.options[index];
            if (fieldInput) fieldInput.checked = true;
          });
        } else {
          select.value = button.dataset.value;
        }
        select.dispatchEvent(new Event('change', { bubbles: true }));
        picker.querySelectorAll('.sierra-option-fieldset').forEach(updateChoiceGroup);
      });
      choices.appendChild(button);
    });

    fieldset.classList.add('sierra-option-fieldset');
    if (isSize) fieldset.classList.add('sierra-option-fieldset--size');
    if (isColor) fieldset.classList.add('sierra-option-fieldset--color');
    if (isLarge) fieldset.classList.add('sierra-option-fieldset--large');
    fieldset.querySelector('.select')?.classList.add('sierra-native-select');
    fieldset.appendChild(choices);

    if (isLarge && label) {
      const count = document.createElement('span');
      count.className = 'sierra-option-count';
      label.appendChild(count);
    }

    fieldset.dataset.sierraChoices = 'true';

    if (isSize && label && !label.querySelector('.sierra-size-guide')) {
      const guide = document.createElement('button');
      guide.type = 'button';
      guide.className = 'sierra-size-guide';
      guide.textContent = 'Find your size';
      guide.addEventListener('click', () => {
        const openGeometry = (attempt = 0) => {
          const geometry = document.querySelector('[data-section-key="geometry"]');
          const tab = document.querySelector('.frengee-tab-item[data-section-source="api"][aria-controls*="geometry"], .frengee-tab-item[aria-controls*="tab-geometry"]');
          if ((!geometry || !tab) && attempt < 20) {
            window.setTimeout(() => openGeometry(attempt + 1), 150);
            return;
          }
          tab?.click();
          window.setTimeout(() => {
            centerSelectedGeometry();
            geometry?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 80);
        };
        openGeometry();
      });
      label.appendChild(guide);
    }

    updateChoiceGroup(fieldset);
    if (isLarge) {
      window.requestAnimationFrame(() => {
        const selected = choices.querySelector('.sierra-option-choice.is-selected');
        if (!selected) return;
        const relativeTop = selected.offsetTop - choices.offsetTop;
        choices.scrollTop = Math.max(0, relativeTop - ((choices.clientHeight - selected.offsetHeight) / 2));
      });
    }
  };

  const enhanceOptions = (product) => {
    const picker = getMainVariantPicker(product);
    if (!picker) return;
    picker.querySelectorAll('.product-form__input--dropdown').forEach((fieldset) => enhanceOption(picker, fieldset));
    picker.querySelectorAll('.sierra-option-fieldset').forEach(updateChoiceGroup);
  };

  const selectedSize = (product) => {
    const fieldset = getMainVariantPicker(product)?.querySelector('[data-handle*="size"], [data-handle*="frame"]');
    return clean(fieldset?.querySelector('select')?.value || fieldset?.querySelector('input:checked')?.value);
  };

  const selectGeometrySize = (value) => {
    document.querySelectorAll('[data-section-key="geometry"] table').forEach((table) => {
      const headerRow = table.tHead?.rows?.[0] || table.rows?.[0];
      const index = value
        ? [...(headerRow?.cells || [])].findIndex((cell) => clean(cell.textContent).toLowerCase() === value.toLowerCase())
        : -1;
      [...table.rows].forEach((row) => {
        [...row.cells].forEach((cell) => cell.classList.toggle('sierra-geometry-selected', index > 0 && cell.cellIndex === index));
      });
    });
  };

  const updateStickySummary = (product) => {
    const stickyInfo = product.querySelector('.product-add-to-cart-sticky--info');
    const picker = getMainVariantPicker(product);
    if (!stickyInfo || !picker) return;
    let summary = stickyInfo.querySelector('.sierra-sticky-selection');
    if (!summary) {
      summary = document.createElement('span');
      summary.className = 'sierra-sticky-selection';
      stickyInfo.appendChild(summary);
    }
    const values = [...picker.querySelectorAll('fieldset')].map((fieldset) => {
      const value = clean(fieldset.querySelector('select')?.value || fieldset.querySelector('input:checked')?.value);
      return fieldset.classList.contains('sierra-option-fieldset--large') ? conciseOption(value) : value;
    }).filter(Boolean);
    summary.textContent = values.join(' · ');
  };

  const updateGalleryCount = (product) => {
    const gallery = product.querySelector('.product-image-container');
    const slides = [...product.querySelectorAll('.product-images__slide')].filter((slide) => {
      return slide.classList.contains('is-active') || !slide.classList.contains('product-images__slide-item--variant');
    });
    if (!gallery || !slides.length) return;

    let counter = gallery.querySelector('.sierra-gallery-count');
    if (!counter) {
      counter = document.createElement('span');
      counter.className = 'sierra-gallery-count';
      counter.setAttribute('aria-live', 'polite');
      gallery.prepend(counter);
    }
    const selected = slides.findIndex((slide) => slide.classList.contains('is-selected') || slide.classList.contains('is-initial-selected'));
    counter.textContent = `${Math.max(1, selected + 1)} / ${slides.length}`;
  };

  const enhanceFinancing = (product) => {
    const info = product.querySelector('.product-information--inner');
    const title = info?.querySelector('.product-title-container');
    const terms = title?.querySelector('form.installment');
    const klarna = [...(info?.children || [])].find((element) => /klarna/i.test(element.id));
    if (!info || (!terms && !klarna)) return;

    let disclosure = info.querySelector('.sierra-financing');
    if (!disclosure) {
      disclosure = document.createElement('section');
      disclosure.className = 'sierra-financing';
      disclosure.setAttribute('aria-label', 'Pay over time options');
      disclosure.innerHTML = '<div class="sierra-financing-heading"><span>Pay over time</span><span>Flexible payment options</span></div><div class="sierra-financing-content"></div>';
      title?.after(disclosure);
    }
    const content = disclosure.querySelector('.sierra-financing-content');
    if (terms && !content.contains(terms)) content.appendChild(terms);
    if (klarna && !content.contains(klarna)) content.appendChild(klarna);
  };

  const enhanceShare = (product) => {
    const share = product.querySelector('.product-information--inner > social-share.share-article');
    if (!share || share.closest('.sierra-share-disclosure')) return;
    const details = document.createElement('details');
    details.className = 'sierra-share-disclosure';
    const summary = document.createElement('summary');
    summary.textContent = 'Share this product';
    share.before(details);
    details.append(summary, share);
  };

  const addTrustStrip = (product) => {
    const host = product.querySelector('.product--product-form');
    const form = host?.querySelector('product-form');
    if (!host || !form || host.querySelector('.sierra-purchase-assurances')) return;
    const assurances = document.createElement('div');
    assurances.className = 'sierra-purchase-assurances';
    assurances.innerHTML = '<span>Secure checkout</span><span>Expert bike support</span><span>Live fulfillment</span>';
    form.after(assurances);
  };

  const updateAvailability = (product) => {
    const info = product.querySelector('.product-information--inner');
    const formHost = info?.querySelector('.product--product-form');
    if (!info || !formHost) return;
    let status = info.querySelector('.sierra-buy-availability');
    if (!status) {
      status = document.createElement('div');
      status.className = 'sierra-buy-availability';
      formHost.before(status);
    }

    const addButton = formHost.querySelector('.single-add-to-cart-button');
    const panel = formHost.querySelector('.sierra-pickup-panel');
    const pickupOnly = panel?.dataset.policy === 'pickup-only';
    const pickupAvailable = panel?.querySelector('[data-sierra-pickup-option="store"][data-state="available"]');
    const shippingAvailable = panel?.querySelector('[data-sierra-pickup-option="warehouse"][data-state="available"]');
    let title = 'Available to order';
    let detail = 'Fulfillment options are shown below';
    let state = 'order';

    if (addButton?.disabled) {
      title = 'Selected option unavailable';
      detail = 'Choose another size or color';
      state = 'unavailable';
    } else if (pickupOnly) {
      title = 'In-store pickup only';
      detail = pickupAvailable ? 'Pickup available in Sparks' : 'Pickup timing shown below';
      state = pickupAvailable ? 'available' : 'order';
    } else if (shippingAvailable && pickupAvailable) {
      title = 'Shipping or store pickup available';
      detail = 'Choose your preferred fulfillment method';
      state = 'available';
    } else if (shippingAvailable) {
      title = 'Shipping available';
      detail = 'Delivery timing shown below';
      state = 'available';
    } else if (pickupAvailable) {
      title = 'Store pickup available';
      detail = 'Pickup at Sierra Bicycle Supply';
      state = 'available';
    }

    const currentTitle = clean(status.querySelector('strong')?.textContent);
    const currentDetail = clean(status.querySelector('small')?.textContent);
    status.dataset.state = state;
    if (currentTitle !== title || currentDetail !== detail) {
      status.innerHTML = `<span class="sierra-buy-availability-dot" aria-hidden="true"></span><span><strong>${title}</strong><small>${detail}</small></span>`;
    }
  };

  const enhanceProduct = (product) => {
    const info = product.querySelector('.product-information--inner');
    if (!info) return;
    info.classList.add('sierra-purchase-panel');
    product.classList.add('sierra-product-ready');
    enhanceOptions(product);
    enhanceFinancing(product);
    enhanceShare(product);
    addTrustStrip(product);
    updateAvailability(product);
    updateStickySummary(product);
    updateGalleryCount(product);
    selectGeometrySize(selectedSize(product));
  };

  const observeProduct = (product) => {
    if (product.dataset.sierraProductObserved === 'true') return;
    product.dataset.sierraProductObserved = 'true';

    let scheduled = false;
    const observer = new MutationObserver((records) => {
      const relevant = records.some((record) => {
        if (record.type === 'childList') return true;
        if (record.attributeName !== 'class') return true;
        return record.target.matches?.('.product-images__slide');
      });
      if (!relevant || scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        enhanceProduct(product);
      });
    });
    observer.observe(product, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'disabled', 'data-state', 'data-policy']
    });
  };

  const initialize = () => document.querySelectorAll(productSelector).forEach((product) => {
    enhanceProduct(product);
    observeProduct(product);
  });

  document.addEventListener('product:variant-change', () => {
    window.requestAnimationFrame(() => document.querySelectorAll(productSelector).forEach(enhanceProduct));
  });
  document.addEventListener('change', (event) => {
    if (!event.target.closest('variant-selects')) return;
    window.requestAnimationFrame(initialize);
  });

  initialize();
})();
